import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Rocket, X } from "lucide-react";
import { Reveal } from "./Motion";
import { useLang } from "@/lib/i18n";

/* Titres dont les lettres bougent doucement — indique que c'est cliquable */
function WavyText({ text }) {
  return (
    <span aria-hidden="true" className="rm-wavy">
      {text.split("").map((ch, i) => (
        <span key={i} style={{ animationDelay: `${(i % 24) * 0.07}s` }}>
          {ch === " " ? "\u00A0" : ch}
        </span>
      ))}
    </span>
  );
}

/* Scène enfantine pour 2028 : une maison, des arbres, un chemin */
function HouseScene() {
  return (
    <svg
      viewBox="0 0 320 150"
      className="w-full h-auto"
      role="img"
      aria-label="Une maison entourée d'arbres, au bout d'un chemin"
      data-testid="roadmap-house-scene"
    >
      {/* soleil */}
      <circle cx="282" cy="26" r="16" fill="hsl(var(--accent))" />
      <g stroke="hsl(var(--accent))" strokeWidth="3" strokeLinecap="round">
        <line x1="282" y1="2" x2="282" y2="8" />
        <line x1="306" y1="26" x2="300" y2="26" />
        <line x1="299" y1="9" x2="295" y2="13" />
        <line x1="265" y1="9" x2="269" y2="13" />
      </g>
      {/* nuages */}
      <g fill="#ffffff" stroke="hsl(var(--foreground) / 0.12)" strokeWidth="1.5">
        <ellipse cx="52" cy="24" rx="20" ry="9" />
        <ellipse cx="66" cy="19" rx="13" ry="8" />
        <ellipse cx="190" cy="16" rx="16" ry="7" />
      </g>
      {/* colline */}
      <path d="M0 118 Q 80 96 160 112 T 320 108 L 320 150 L 0 150 Z" fill="hsl(var(--sky))" />
      <path d="M0 130 Q 90 112 190 126 T 320 122 L 320 150 L 0 150 Z" fill="#D9EBD3" />
      {/* chemin vers la maison */}
      <path
        d="M160 148 C 150 132 176 126 166 112 C 158 100 168 94 163 86"
        fill="none"
        stroke="hsl(var(--coral))"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="1 9"
      />
      {/* maison */}
      <g className="rm-house" style={{ transformOrigin: "163px 90px" }}>
        <rect x="136" y="62" width="54" height="34" rx="4" fill="#FFF6E8" stroke="hsl(var(--foreground))" strokeWidth="2.5" />
        <path d="M130 64 L163 38 L196 64 Z" fill="hsl(var(--coral))" stroke="hsl(var(--foreground))" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="156" y="74" width="14" height="22" rx="3" fill="hsl(var(--primary))" stroke="hsl(var(--foreground))" strokeWidth="2" />
        <circle cx="146" cy="72" r="5" fill="hsl(var(--sky))" stroke="hsl(var(--foreground))" strokeWidth="2" />
        <circle cx="180" cy="72" r="5" fill="hsl(var(--sky))" stroke="hsl(var(--foreground))" strokeWidth="2" />
        {/* fumée */}
        <circle className="rm-smoke" cx="183" cy="34" r="3.5" fill="hsl(var(--foreground) / 0.25)" />
        <circle className="rm-smoke" style={{ animationDelay: "0.6s" }} cx="187" cy="26" r="2.5" fill="hsl(var(--foreground) / 0.18)" />
      </g>
      {/* arbres */}
      <g stroke="hsl(var(--foreground))" strokeWidth="2.5">
        <line x1="96" y1="112" x2="96" y2="92" stroke="#8A5A3B" strokeWidth="5" strokeLinecap="round" />
        <circle cx="96" cy="82" r="16" fill="#7FB069" />
        <circle cx="86" cy="90" r="9" fill="#94C47D" stroke="none" />
        <line x1="238" y1="114" x2="238" y2="96" stroke="#8A5A3B" strokeWidth="5" strokeLinecap="round" />
        <circle cx="238" cy="86" r="14" fill="#7FB069" />
        <circle cx="248" cy="94" r="8" fill="#94C47D" stroke="none" />
      </g>
      {/* fleurs */}
      <g>
        <circle cx="60" cy="124" r="4" fill="hsl(var(--coral))" />
        <circle cx="272" cy="120" r="4" fill="hsl(var(--accent))" />
        <circle cx="120" cy="134" r="3.5" fill="hsl(var(--plum))" />
      </g>
    </svg>
  );
}

export default function Roadmap() {
  const { t } = useLang();
  const [activeItem, setActiveItem] = useState(null);
  const [hintDone, setHintDone] = useState(false);

  const STEPS = [
    {
      n: "1",
      dateKey: "roadmap.step_1_date",
      color: "hsl(var(--primary))",
      bg: "hsl(var(--sky))",
      icon: Sparkles,
      items: [
        { id: "ecole", titleKey: "rm.ecole.title", descKey: "rm.ecole.desc" },
        { id: "psy", titleKey: "rm.psy.title", descKey: "rm.psy.desc" },
        { id: "ateliers", titleKey: "rm.ateliers.title", descKey: "rm.ateliers.desc" },
        { id: "prep_logo", titleKey: "rm.prep_logo.title", descKey: "rm.prep_logo.desc" },
      ],
    },
    {
      n: "2",
      dateKey: "roadmap.step_2_date",
      color: "hsl(var(--coral))",
      bg: "#FFE5DE",
      icon: Rocket,
      items: [
        { id: "logo", titleKey: "rm.logo.title", descKey: "rm.logo.desc" },
        { id: "psy_rel", titleKey: "rm.psy_rel.title", descKey: "rm.psy_rel.desc" },
        { id: "compo", titleKey: "rm.compo.title", descKey: "rm.compo.desc" },
        { id: "acc_parents", titleKey: "rm.acc_parents.title", descKey: "rm.acc_parents.desc" },
      ],
    },
    {
      n: "3",
      dateKey: "roadmap.step_3_date",
      color: "hsl(var(--foreground))",
      bg: "hsl(var(--accent))",
      icon: null,
      house: true,
      items: [
        { id: "local", titleKey: "rm.local.title", descKey: "rm.local.desc" },
        { id: "more_kids", titleKey: "rm.more_kids.title", descKey: "rm.more_kids.desc" },
        { id: "equipe", titleKey: "rm.equipe.title", descKey: "rm.equipe.desc" },
        { id: "new_act", titleKey: "rm.new_act.title", descKey: "rm.new_act.desc" },
      ],
    },
  ];

  const handleOpen = (it, s) => {
    setHintDone(true);
    setActiveItem({ ...it, step: s });
  };

  return (
    <section id="roadmap" className="py-16 md:py-24 relative overflow-hidden" data-testid="roadmap-section">
      {/* Fond illustré : ciel, collines, confettis */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-x-0 bottom-0 h-40 bg-[#EAF3E4]" />
        <svg className="absolute inset-x-0 bottom-0 w-full h-48" viewBox="0 0 1440 190" preserveAspectRatio="none">
          <path d="M0 90 Q 240 30 480 75 T 960 70 T 1440 85 L 1440 190 L 0 190 Z" fill="hsl(var(--sky))" opacity="0.8" />
          <path d="M0 130 Q 260 80 560 120 T 1120 115 T 1440 125 L 1440 190 L 0 190 Z" fill="#DCEDCE" />
        </svg>
        <div className="absolute top-16 left-[6%] w-3 h-3 rounded-full dot-yellow" />
        <div className="absolute top-40 right-[8%] w-2 h-2 rounded-full dot-coral" />
        <div className="absolute bottom-56 left-[45%] w-2.5 h-2.5 rounded-full dot-sage" />
        <div className="absolute bottom-44 right-[15%] w-2 h-2 rounded-full dot-plum" />
        <svg className="absolute top-24 right-[18%] w-16 h-8 opacity-70" viewBox="0 0 64 32">
          <ellipse cx="24" cy="18" rx="18" ry="8" fill="#fff" />
          <ellipse cx="38" cy="13" rx="12" ry="7" fill="#fff" />
        </svg>
        <svg className="absolute top-52 left-[14%] w-12 h-6 opacity-60" viewBox="0 0 64 32">
          <ellipse cx="24" cy="18" rx="18" ry="8" fill="#fff" />
          <ellipse cx="38" cy="13" rx="12" ry="7" fill="#fff" />
        </svg>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <div className="chip mx-auto">{t("roadmap.chip")}</div>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              {t("roadmap.title_1")}{" "}
              <span className="text-primary">{t("roadmap.title_plan")}</span>
              {t("roadmap.title_rest")}
            </h2>
            <p className="mt-5 text-lg text-foreground/75 leading-relaxed max-w-2xl mx-auto">
              {t("roadmap.subtitle")}
            </p>
            <p className="mt-4 text-sm font-semibold text-primary/80" data-testid="roadmap-click-hint">
              ↓ {t("roadmap.click_hint")}
            </p>
          </div>
        </Reveal>

        <div className="mt-12 md:mt-16 relative">
          <svg
            className="hidden lg:block absolute left-0 right-0 mx-auto top-24 w-[85%] h-24 -z-0"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M 40 60 Q 250 -10 500 50 T 960 60"
              fill="none"
              stroke="hsl(var(--foreground))"
              strokeWidth="2"
              strokeDasharray="4 8"
              opacity="0.35"
            />
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative z-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <article className="relative flex flex-col items-center text-center h-full" data-testid={`roadmap-step-${s.n}`}>
                  <div
                    className="w-14 h-14 rounded-full grid place-items-center font-heading text-2xl text-white shadow-lg mb-3"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  >
                    {s.n}
                  </div>

                  <div className="w-full rounded-3xl overflow-hidden bg-white border border-border h-full flex flex-col shadow-[0_10px_30px_-15px_rgba(23,49,58,0.2)]">
                    {s.house && (
                      <div className="bg-[#FDF9EF] border-b border-border px-4 pt-3">
                        <HouseScene />
                      </div>
                    )}
                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <div
                        className="inline-flex items-center gap-2 self-center px-4 py-2 rounded-full text-sm font-bold"
                        style={{ backgroundColor: s.bg, color: s.color }}
                      >
                        {s.icon && <s.icon className="w-4 h-4" strokeWidth={2.5} />}
                        {t(s.dateKey)}
                      </div>

                      <ul className="mt-5 space-y-2">
                        {s.items.map((it, j) => (
                          <li key={it.id} className="relative">
                            {i === 0 && j === 0 && !hintDone && (
                              <span
                                className="rm-tap-bubble absolute -top-9 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg whitespace-nowrap"
                                style={{ backgroundColor: "hsl(var(--coral))" }}
                                data-testid="roadmap-tap-bubble"
                                aria-hidden="true"
                              >
                                {t("roadmap.tap_bubble")}
                              </span>
                            )}
                            <motion.button
                              onClick={() => handleOpen(it, s)}
                              whileTap={{ scale: 0.95 }}
                              data-testid={`roadmap-item-${it.id}`}
                              aria-label={t(it.titleKey)}
                              className="w-full group flex items-start gap-2.5 text-left px-2 py-1.5 -mx-2 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                            >
                              <span
                                className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 rm-dot-pulse"
                                style={{ backgroundColor: s.color, animationDelay: `${j * 0.4}s` }}
                              />
                              <span className="text-base text-foreground/85 leading-relaxed group-hover:text-primary">
                                <span className="sr-only">{t(it.titleKey)}</span>
                                <WavyText text={t(it.titleKey)} />
                              </span>
                            </motion.button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div
                    className="mt-2 w-32 h-3 rounded-full opacity-70"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  />
                </article>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.4}>
          <p className="mt-14 text-center text-sm text-foreground/60 max-w-xl mx-auto italic">
            {t("roadmap.footer")}
          </p>
        </Reveal>
      </div>

      <RoadmapItemDialog item={activeItem} onClose={() => setActiveItem(null)} />
    </section>
  );
}

function RoadmapItemDialog({ item, onClose }) {
  const { t } = useLang();

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-foreground/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={onClose}
          data-testid="roadmap-item-dialog"
        >
          <motion.div
            initial={{ y: 50, opacity: 0.8, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-lg bg-background rounded-t-3xl md:rounded-3xl max-h-[85vh] overflow-y-auto"
          >
            <div className="px-6 md:px-8 py-6 flex items-start justify-between border-b border-border">
              <div>
                <div
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: item.step.bg, color: item.step.color }}
                  data-testid="roadmap-dialog-step"
                >
                  {t("roadmap.dialog_step_label")} {item.step.n} · {t(item.step.dateKey)}
                </div>
                <h3 className="mt-3 font-heading text-2xl md:text-3xl leading-tight" data-testid="roadmap-dialog-title">
                  {t(item.titleKey)}
                </h3>
              </div>
              <button
                onClick={onClose}
                data-testid="roadmap-dialog-close"
                aria-label={t("roadmap.dialog_close")}
                className="p-2.5 rounded-full border border-border hover:bg-muted shrink-0 ml-3"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 md:px-8 py-6">
              <p className="text-base md:text-lg text-foreground/85 leading-relaxed" data-testid="roadmap-dialog-desc">
                {t(item.descKey)}
              </p>
              <button
                onClick={onClose}
                className="mt-6 pill-btn bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t("roadmap.dialog_close")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
